import { useMemo, useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Search,
} from 'lucide-react';

export type AbaInstrucaoId =
  | 'projeto'
  | 'catalogo'
  | 'historico'
  | 'relatorio'
  | 'sobra'
  | 'produtoPorPalete'
  | 'tempoCorte'
  | 'lotes'
  | 'analiseTempo'
  | 'adiantamentoMdf'
  | 'boletimPontuacao'
  | 'tempoEmbalar'
  | 'boletimCalculadora'
  | 'boletimLinhaFabrica'
  | 'boletimTabelaDias'
  | 'boletimResumoDias'
  | 'boletimGrafico'
  | 'resultadosCopa';

interface BlocoInstrucao {
  id: AbaInstrucaoId;
  titulo: string;
  resumo: string;
  topicos: string[];
  formulas?: string[];
  dicas?: string[];
}

interface CategoriaInstrucao {
  id: string;
  titulo: string;
  cor: string;
  abas: BlocoInstrucao[];
}

const CATEGORIAS: CategoriaInstrucao[] = [
  {
    id: 'plano',
    titulo: 'Plano de corte',
    cor: '#009b3a',
    abas: [
      {
        id: 'projeto',
        titulo: 'Projeto',
        resumo: 'Montagem do plano de corte: peças, chapas, layout, impressão e visualização.',
        topicos: [
          'Cadastre peças (código, medidas, quantidade, material).',
          'Configure plano (quantidade de chapas, etapas, tipo de material).',
          'Visualize o corte na folha e ajuste rotação, caixas de texto e observações.',
          'Salve no catálogo ou imprima o plano para a serra.',
          'Caixas de texto no desenho aparecem na impressão com quebra de linha automática.',
        ],
        dicas: [
          'Salve o projeto antes de imprimir para não perder alterações.',
          'Use “Resetar Projeto” só quando quiser começar do zero.',
        ],
      },
      {
        id: 'catalogo',
        titulo: 'Catálogo',
        resumo: 'Biblioteca de projetos salvos para reabrir, duplicar ou excluir.',
        topicos: [
          'Cada card é um projeto completo (peças + layout).',
          'Abra um projeto para carregá-lo na aba Projeto.',
          'Organize por nome; útil para produtos que se repetem.',
        ],
      },
      {
        id: 'historico',
        titulo: 'Histórico',
        resumo: 'Registro de impressões e projetos enviados à serra.',
        topicos: [
          'Lista o que foi impresso, com data/hora e nome do produto.',
          'Consulte reimpressões e auditoria de uso.',
        ],
      },
      {
        id: 'relatorio',
        titulo: 'Relatório',
        resumo: 'Lista de peças do corte atual com totais, metros e opção de colar do Excel.',
        topicos: [
          'Mostra todas as peças lançadas no projeto ativo.',
          'Importe linhas coladas do Excel (modal “Colar Excel”).',
          'Base para conferência antes da produção.',
        ],
      },
      {
        id: 'sobra',
        titulo: 'Sobra',
        resumo: 'Peças que sobraram de chapas — cadastro, pastas, riscar e reaproveitar.',
        topicos: [
          'Cadastre sobras com código, medida, quantidade e lote de origem.',
          'Organize em pastas; riscar remove da lista ativa sem apagar histórico.',
        ],
      },
      {
        id: 'produtoPorPalete',
        titulo: 'Produto por Palete',
        resumo: 'Montagem de paletes por lote: cores, volumes e quantidades por caixa.',
        topicos: [
          'Configure cores e volumes (1, 2 ou 3) por produto.',
          'Busque por lote e informe quantidades por cor/volume.',
          'Impressão de etiquetas/listas de palete.',
          'Volumes definidos aqui alimentam a aba Tempo de Embalagem.',
        ],
      },
    ],
  },
  {
    id: 'tempo',
    titulo: 'Tempo de produção',
    cor: '#009b3a',
    abas: [
      {
        id: 'tempoCorte',
        titulo: 'Tempo de Corte',
        resumo: 'Cronometragem do corte na serra — tempo real por produto/lote.',
        topicos: [
          'Inicie/pare cronômetro ou informe tempo manualmente.',
          'Salve cards por produto; envie para Análise de Tempo se quiser comparar depois.',
          'Metros² e material podem ser detalhados por item do card.',
        ],
        dicas: [
          'Use “tempo exato” e quantidade correta — erros aqui propagam para todo o boletim.',
        ],
      },
      {
        id: 'lotes',
        titulo: 'Lotes',
        resumo: 'Cadastro de lotes em produção: produto, quantidade, cor, status e fluxo.',
        topicos: [
          'Cada lote liga produto + quantidade + datas de corte/embalagem.',
          'Status: cadastrado, em corte, em embalagem, finalizado.',
          'Base para Produto por Palete e Boletim.',
        ],
      },
      {
        id: 'analiseTempo',
        titulo: 'Análise de Tempo',
        resumo: 'Compara tempos salvos do corte com metas e histórico por produto.',
        topicos: [
          'Produtos enviados da aba Tempo de Corte aparecem aqui.',
          'Veja % da meta, tempo por unidade e evolução.',
        ],
      },
      {
        id: 'adiantamentoMdf',
        titulo: 'Adiantamento de MDF',
        resumo:
          'Peças MDF dos produtos do Tempo de Corte, filtrados pela data de início, para laka borda, usinagem, PU, coladeira e furadeira.',
        topicos: [
          'A data “a partir de” só filtra quais produtos você pode incluir. Marque vários na lista ao mesmo tempo.',
          'Variantes do mesmo produto (Painel Estone 1.4 e 1.8) com a mesma descrição (PRAT SUP) viram uma linha: PAINEL ESTONE 1.4 e 1.8.',
          'Em cada linha coloque a data de adiantar — peças diferentes podem ser dias diferentes.',
          'As peças MDF entram sozinhas. Exclua a linha que não adianta (não apaga o Tempo de Corte).',
          'Se precisar, inclua MDF ou aglomerado do produto, ou adicione uma peça nova.',
          'A descrição não mostra a cor e pode ser editada (vale também no Tempo de Corte).',
          'A lista (produtos, unificação e datas) fica gravada em segundo plano.',
          'Imprimir lista: cabeçalho da função (Corte, Laka borda…) e, em cada data, uma linha compacta com as peças e o produto.',
        ],
      },
    ],
  },
  {
    id: 'boletim',
    titulo: 'Boletim',
    cor: '#009b3a',
    abas: [
      {
        id: 'boletimPontuacao',
        titulo: 'Boletim Pontuação',
        resumo: 'Cadastro de pontos (m²) por produto — coração do sistema de meta diária.',
        topicos: [
          'Cada produto tem uma pontuação (ex.: FOX = 0,54 · VIGO = 2,00).',
          'Meta padrão do dia: 1600 pontos em 528 minutos (8h48).',
          'Produtos/dia esperados = 1600 ÷ pontuação do produto.',
          'Marcar “fora de linha” remove o produto de listas ativas (ex.: Tempo de Embalagem).',
          'Alterações aqui atualizam na hora Tempo de Embalagem e demais abas.',
        ],
        formulas: ['Peças/dia (teórico) = 1600 ÷ P'],
      },
      {
        id: 'tempoEmbalar',
        titulo: 'Tempo de Embalagem',
        resumo:
          'Cronometragem da embalagem por produto/volume + análise se a pontuação está certa.',
        topicos: [
          'Informe tempo (ex.: 00:02:11,28) e quantidade (ex.: 10) por volume.',
          'O app calcula peças/seg, min, h e dia (com acréscimo % configurável).',
          'Produtos vêm da Pontuação (ativos); volumes 1/2/3 por produto.',
          'Dados salvos no navegador (localStorage + IndexedDB).',
        ],
        formulas: [
          'Tempo ajustado = tempo medido × (1 + acréscimo% ÷ 100)',
          'Peças/min = quantidade ÷ (tempo ajustado em minutos)',
          'Peças/dia = peças/min × jornada (528 min padrão)',
          '1 volume: Pont. sugerida = 1600 ÷ peças/dia medidas',
          '2–3 volumes: P_vol = P × (min/peça vol ÷ soma min/peça)',
          'P ideal (conjunto) = soma das Pont. sugeridas de todos os volumes',
          'Tempo extra = jornada × (P ideal ÷ P cadastro − 1)',
        ],
        dicas: [
          'Coluna “Análise por volume”: compara cada fatia com o ritmo daquele volume.',
          'Bloco “Conjunto”: use P ideal para mudar na Pontuação (ex.: 2,00 → 2,38).',
          '+Tempo positivo + rótulo Excede (vermelho/laranja): não fecha na jornada — ruim, revise pontuação ou ritmo.',
          '+Tempo negativo + rótulo Sobra (verde): termina antes — bom, sobra tempo no dia.',
          'Tempo p/ meta: jornada + extra (ex.: 10h28 para 800 produtos no ritmo do VIGO).',
          'Todos os volumes precisam de tempo preenchido para o bloco Conjunto calcular.',
        ],
      },
      {
        id: 'boletimCalculadora',
        titulo: 'Boletim Calculadora',
        resumo: 'Simula o dia de produção: distribui pontos entre produtos e vê se fecha 1600.',
        topicos: [
          'Monte o mix do dia (produtos + quantidades).',
          'Soma pontos = Σ (pontuação × quantidade).',
          'Veja se ultrapassa ou falta meta do dia.',
        ],
        formulas: ['Pontos usados = Σ (P do produto × quantidade produzida)'],
      },
      {
        id: 'boletimLinhaFabrica',
        titulo: 'Linha de Pintura',
        resumo: 'Boletim da linha de pintura — produtos, cores, cabeçalhos e impressão.',
        topicos: [
          'Blocos por produto/cor com linhas arrastáveis.',
          'Integra tempos de corte salvos e lotes.',
          'Impressão formatada para o chão de fábrica.',
        ],
      },
      {
        id: 'boletimTabelaDias',
        titulo: 'Boletim Tabela Dias',
        resumo: 'Tabela mensal de produção/pontos por dia.',
        topicos: [
          'Visão calendário com totais por dia.',
          'Preenchimento manual ou importado do fluxo do boletim.',
        ],
      },
      {
        id: 'boletimResumoDias',
        titulo: 'Boletim Resumo Dias',
        resumo: 'Resumo consolidado de vários dias — médias e totais.',
        topicos: [
          'Compare semanas e veja média de pontos/dia.',
          'Complementa a Tabela Dias e o Gráfico.',
        ],
      },
      {
        id: 'boletimGrafico',
        titulo: 'Boletim Gráfico',
        resumo: 'Gráfico de evolução da produção (janela de 13 meses).',
        topicos: [
          'Visualize tendência de pontos ou produção ao longo do tempo.',
          'Ajuste o mês inicial da janela; redimensione o gráfico arrastando.',
        ],
      },
    ],
  },
  {
    id: 'outros',
    titulo: 'Copa',
    cor: '#be123c',
    abas: [
      {
        id: 'resultadosCopa',
        titulo: 'Resultados da Copa',
        resumo: 'Bolão/copa — placares, classificação e palpites (link público).',
        topicos: [
          'Atualize resultados dos jogos.',
          'Participantes palpitam pelo site público.',
          'Classificação e mata-mata quando configurado.',
        ],
      },
    ],
  },
];

interface InstrucoesSectionProps {
  onIrParaAba?: (id: AbaInstrucaoId) => void;
}

export default function InstrucoesSection({ onIrParaAba }: InstrucoesSectionProps) {
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<Set<string>>(() => new Set(['plano']));

  const categoriasFiltradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return CATEGORIAS;
    return CATEGORIAS.map((cat) => ({
      ...cat,
      abas: cat.abas.filter(
        (a) =>
          a.titulo.toLowerCase().includes(q) ||
          a.resumo.toLowerCase().includes(q) ||
          a.topicos.some((t) => t.toLowerCase().includes(q)) ||
          (a.dicas?.some((d) => d.toLowerCase().includes(q)) ?? false)
      ),
    })).filter((cat) => cat.abas.length > 0);
  }, [busca]);

  const toggleCat = (id: string) => {
    setAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalAbas = CATEGORIAS.reduce((s, c) => s + c.abas.length, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-black flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#009b3a]" />
            Instruções do Sistema
          </h3>
          <p className="text-sm text-neutral-600 mt-1 max-w-3xl">
            Manual de referência das <strong className="text-black">{totalAbas} abas</strong>.
            Use a busca ou abra cada módulo. Quando disponível, clique em{' '}
            <strong className="text-black">Ir para aba</strong> para abrir direto.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[#009b3a]/20 bg-[#f0faf4] p-4 text-sm">
        <p className="font-semibold text-black">Conceitos que aparecem em várias abas</p>
        <ul className="mt-2 space-y-1 text-xs text-neutral-700 list-disc pl-4">
          <li>
            <strong className="text-black">1600 pontos</strong> = meta do dia ·{' '}
            <strong className="text-black">528 min</strong> = jornada (8h48).
          </li>
          <li>
            <strong className="text-black">Pontuação (P)</strong> = “peso” do produto no dia. Quanto
            maior P, menos unidades cabem em 1600 pontos.
          </li>
          <li>
            <strong className="text-black">Δ Tempo (embalagem):</strong>{' '}
            <span className="text-emerald-700 font-semibold">Sobra</span> (negativo) = termina antes —{' '}
            <strong className="text-black">bom</strong>.{' '}
            <span className="text-red-700 font-semibold">Excede</span> (positivo) = passa da jornada —{' '}
            <strong className="text-black">ruim</strong>.
          </li>
        </ul>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar aba ou palavra-chave..."
          className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm bg-white"
        />
      </div>

      <div className="space-y-3">
        {categoriasFiltradas.length === 0 ? (
          <p className="text-center text-neutral-600 py-8">Nenhuma instrução encontrada.</p>
        ) : (
          categoriasFiltradas.map((cat) => {
            const expandido = abertos.has(cat.id);
            return (
              <div
                key={cat.id}
                className="rounded-xl border border-[#009b3a]/15 bg-[#fafbfc] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleCat(cat.id)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-[#eef0f4] transition"
                >
                  {expandido ? (
                    <ChevronDown className="w-4 h-4 shrink-0" style={{ color: cat.cor }} />
                  ) : (
                    <ChevronRight className="w-4 h-4 shrink-0" style={{ color: cat.cor }} />
                  )}
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: cat.cor }}
                  >
                    {cat.titulo}
                  </span>
                  <span className="text-xs text-neutral-500">({cat.abas.length})</span>
                </button>
                {expandido && (
                  <div className="border-t border-[#009b3a]/10 divide-y divide-[#009b3a]/10">
                    {cat.abas.map((aba) => (
                      <article key={aba.id} className="p-4 space-y-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <h4 className="font-bold text-black text-base">{aba.titulo}</h4>
                            <p className="text-sm text-neutral-600 mt-0.5">{aba.resumo}</p>
                          </div>
                          {onIrParaAba && (
                            <button
                              type="button"
                              onClick={() => onIrParaAba(aba.id)}
                              className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#009b3a]/30 bg-[#e8f5ec] text-xs font-semibold text-[#007a2e] hover:bg-[#d4f0e0]"
                            >
                              Ir para aba
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <div>
                          <p className="text-[10px] font-bold uppercase text-neutral-500 mb-1">
                            Como funciona
                          </p>
                          <ul className="text-sm text-neutral-800 space-y-1 list-disc pl-4">
                            {aba.topicos.map((t, i) => (
                              <li key={i}>{t}</li>
                            ))}
                          </ul>
                        </div>
                        {aba.formulas && aba.formulas.length > 0 && (
                          <div className="rounded-lg bg-[#eef0f4] border border-[#009b3a]/10 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase text-[#009b3a] mb-1">
                              Fórmulas
                            </p>
                            <ul className="text-xs font-mono text-black space-y-0.5">
                              {aba.formulas.map((f, i) => (
                                <li key={i}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {aba.dicas && aba.dicas.length > 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2">
                            <p className="text-[10px] font-bold uppercase text-amber-800 mb-1">
                              Dicas
                            </p>
                            <ul className="text-xs text-amber-950 space-y-1 list-disc pl-4">
                              {aba.dicas.map((d, i) => (
                                <li key={i}>{d}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
